/**
 * DogFlagChips — Ted #28 (#1050). The ONE renderer for the flags that follow
 * a dog through its stay: transport (Phase 1), the vaccine shield, INTACT,
 * meds, and the owner's balance / credit / agreement (Phase 2).
 *
 * Ted (HTHD, 2026-09-21): "a highly visible symbol, color or icon to appear
 * anywhere that dog is seen in the system." The server derives the list once
 * (`dog-flags.ts`); this component draws it identically on every surface —
 * the dog-first strip, the Front Desk names, the day list, the on-site
 * roster, the profile, the kennel floor, the printed board and run card,
 * the lodging block, the Quick Book picker. A surface that renders dog
 * names without it fails `flag-surface-coverage.test.ts`, not a code review.
 *
 * SCOPE decides WHERE a pill goes, not whether it exists. Ted: money flags
 * "follow underneath the client's name"; the shield and INTACT sit under the
 * dog. So `scope="dog"` (the default) draws the dog's and the stay's flags
 * beside the dog, and `scope="account"` draws the owner's under the owner —
 * two calls on one list, never a `$` beside a dog.
 *
 * DESIGN-PHILOSOPHY: status tokens only (`--ferro-warning` / `--ferro-info` /
 * `--ferro-error`), never a brand colour; and colour is NEVER the only
 * signal — every pill carries its label as text plus a glyph, and the
 * `title` / `aria-label` carry the sentence a new receptionist needs. The
 * server orders severity-first; this component keeps that order so the most
 * urgent pill reads first at 375px where only one may fit.
 */
import type { DogFlag, DogFlagScope } from '../../lib/api.js';

/** `dog` = the dog's own flags AND the stay's (they sit beside the dog). */
export type DogFlagChipsScope = 'dog' | 'account';

interface DogFlagChipsProps {
  flags: readonly DogFlag[] | undefined;
  /** Which flags THIS position draws. Default: beside the dog. */
  scope?: DogFlagChipsScope;
  /** Test-id prefix so a surface's chip can be found in its own tests. */
  testIdPrefix?: string;
}

const SCOPES_FOR: Record<DogFlagChipsScope, ReadonlyArray<DogFlagScope>> = {
  dog: ['dog', 'stay'],
  account: ['account'],
};

const glyphProps = {
  className: 'ferro-dog-flag__glyph',
  viewBox: '0 0 24 24',
  'aria-hidden': true,
  focusable: 'false',
} as const;

/** Line-art van — the transport glyph. Stroke follows currentColor. */
function TransportGlyph(): JSX.Element {
  return (
    <svg {...glyphProps}>
      <path d="M3 7h11v9H3z" />
      <path d="M14 10h4l3 3v3h-7z" />
      <circle cx="7" cy="17.5" r="1.8" />
      <circle cx="17" cy="17.5" r="1.8" />
    </svg>
  );
}

/** Line-art syringe (#546; Johnny 2026-07-22 — the universal vaccination glyph, not Gingr's shield). */
function SyringeGlyph(): JSX.Element {
  return (
    <svg {...glyphProps}>
      <path d="m18 2 4 4" />
      <path d="m17 7 3-3" />
      <path d="M19 9 8.7 19.3c-1 1-2.5 1-3.4 0l-.6-.6c-1-1-1-2.5 0-3.4L15 5" />
      <path d="m9 11 4 4" />
      <path d="m5 19-3 3" />
      <path d="m14 4 6 6" />
    </svg>
  );
}

/** ♂♀ — Gingr's sex/intact icon, as a glyph beside the word. */
function IntactGlyph(): JSX.Element {
  return (
    <svg {...glyphProps}>
      <circle cx="9" cy="14" r="5" />
      <path d="M13 10 20 3" />
      <path d="M15 3h5v5" />
    </svg>
  );
}

/** A question mark in a circle — "ask, never assume". */
function UnknownGlyph(): JSX.Element {
  return (
    <svg {...glyphProps}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/** A pill (capsule) — meds. */
function MedsGlyph(): JSX.Element {
  return (
    <svg {...glyphProps}>
      <rect x="3" y="8" width="18" height="8" rx="4" transform="rotate(-45 12 12)" />
      <path d="m9 9 6 6" />
    </svg>
  );
}

/** `$` — Gingr's balance icon, drawn so it is never a bare glyph without the word. */
function DollarGlyph(): JSX.Element {
  return (
    <svg {...glyphProps}>
      <path d="M12 3v18" />
      <path d="M16.5 7.5A3.5 3.5 0 0 0 13 5h-2a3 3 0 0 0 0 6h2a3 3 0 0 1 0 6h-2a3.5 3.5 0 0 1-3.5-2.5" />
    </svg>
  );
}

/** A signature line with an X — the unsigned agreement. */
function AgreementGlyph(): JSX.Element {
  return (
    <svg {...glyphProps}>
      <path d="M4 20h16" />
      <path d="M6 15c2-4 4-6 6-6s2 4 4 4 3-3 4-3" />
      <path d="m15 4 4 4" />
      <path d="m19 4-4 4" />
    </svg>
  );
}

/** Line-art scissors — grooming on this visit (#1125). */
function GroomingGlyph(): JSX.Element {
  return (
    <svg {...glyphProps}>
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M20 4 8.1 15.9" />
      <path d="M14.5 14.5 20 20" />
      <path d="M8.1 8.1 12 12" />
    </svg>
  );
}

/** A circle with a slash — Do Not Admit, the hard stop (#1125). */
function BannedGlyph(): JSX.Element {
  return (
    <svg {...glyphProps}>
      <circle cx="12" cy="12" r="9" />
      <path d="m5.6 5.6 12.8 12.8" />
    </svg>
  );
}

/** One dot inside a closed run — isolated from the floor (#1125). */
function IsolatedGlyph(): JSX.Element {
  return (
    <svg {...glyphProps}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}

const GLYPH: Record<string, () => JSX.Element> = {
  transport: TransportGlyph,
  vax: SyringeGlyph,
  intact: IntactGlyph,
  altered_unknown: UnknownGlyph,
  meds: MedsGlyph,
  vax_unverified: SyringeGlyph,
  visit_unpaid: DollarGlyph,
  balance: DollarGlyph,
  credit: DollarGlyph,
  agreement: AgreementGlyph,
  // #1125 — the three check-in flags (lane LC) had no glyph and drew as
  // bare words beside every other pill's icon.
  grooming: GroomingGlyph,
  banned: BannedGlyph,
  isolated: IsolatedGlyph,
};

export function DogFlagChips({
  flags,
  scope = 'dog',
  testIdPrefix = '',
}: DogFlagChipsProps): JSX.Element | null {
  if (!Array.isArray(flags) || flags.length === 0) return null;
  const wanted = SCOPES_FOR[scope];
  const shown = flags.filter((f) => wanted.includes(f.scope));
  if (shown.length === 0) return null;
  return (
    <>
      {shown.map((flag) => {
        // An unknown key from a NEWER server (a later phase adds keys before
        // this bundle learns them) still renders as a labelled pill — no
        // glyph, never dropped: the label is the information.
        const Glyph = GLYPH[flag.key] ?? null;
        return (
          <span
            key={flag.key}
            className={`ferro-dog-flag ferro-dog-flag--${flag.severity} ferro-dog-flag--${flag.key}`}
            role="img"
            aria-label={`${flag.label}: ${flag.detail}`}
            title={flag.detail}
            data-testid={`${testIdPrefix}dog-flag-${flag.key}`}
            data-severity={flag.severity}
            data-scope={flag.scope}
            data-requires-arrival-check={flag.requiresArrivalCheck ? 'true' : 'false'}
          >
            {Glyph ? <Glyph /> : null}
            <span className="ferro-dog-flag__label">{flag.label}</span>
          </span>
        );
      })}
    </>
  );
}
